import { Platform } from 'react-native';
import { AsyncStorage } from '@betty/toolkit/storage';
import {
  appLogout,
  createSlice,
  createAction,
  createSelector,
  persistReducer,
  createAsyncThunk,
  AuthenticationStatus,
  injectSlice,
} from '@betty/toolkit/state';
import GamesService from '../../services/GamesService/GamesService';
import { Game, GamesResponse, LaunchGamePayload } from '../shared/games.types';
import { GamesState } from './games.types';
import { TOP_SLOT_PROVIDERS } from '../../components/Search/TopSlotProviders/TopSlotProviders.content';
import { getLockedLabel } from '../../components/Games/Game/Game.utils';
import FavoritesService from '../../services/FavoritesService/FavoritesService';

export const fetchGames = createAsyncThunk<GamesResponse, AuthenticationStatus>(
  'games/fetch',
  async (authStatus) =>
    authStatus === 'authenticated'
      ? await GamesService.getGames()
      : await GamesService.getPublicGames(),
);

export const favoriteGame = createAsyncThunk(
  'games/favorite',
  async (gameId: Game['id']) => {
    await FavoritesService.favorite({ pathParams: { gameId } });
  },
);

export const unfavoriteGame = createAsyncThunk(
  'games/unfavorite',
  async (gameId: Game['id']) => {
    await FavoritesService.unfavorite({ pathParams: { gameId } });
  },
);

export const launchGame = createAction<LaunchGamePayload>('games/launch');

const initialState: GamesState = {
  authenticated: {
    ids: [],
    byId: {},
    dataState: 'idle',
  },
  unauthenticated: {
    ids: [],
    byId: {},
    dataState: 'idle',
  },
};

const games = createSlice({
  name: 'games',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchGames.fulfilled, (state, { meta, payload }) => {
      const { games } = payload;

      state[meta.arg].dataState = 'fulfilled';

      state[meta.arg].ids = Object.keys(games).map(Number);
      state[meta.arg].byId = games;
    });
    builder.addCase(fetchGames.pending, (state, { meta }) => {
      state[meta.arg].dataState = 'pending';
    });
    builder.addCase(fetchGames.rejected, (state, { meta }) => {
      state[meta.arg].dataState = 'rejected';
    });
    builder.addCase(favoriteGame.pending, (state, action) => {
      const gameId = action.meta.arg;
      state.authenticated.byId[gameId].displayFeatures.Favorite = {};
    });
    builder.addCase(unfavoriteGame.pending, (state, action) => {
      const gameId = action.meta.arg;
      delete state.authenticated.byId[gameId].displayFeatures.Favorite;
    });
    builder.addCase(favoriteGame.rejected, (state, action) => {
      // reset favorite status when service call fails
      const gameId = action.meta.arg;
      delete state.authenticated.byId[gameId].displayFeatures.Favorite;
    });
    builder.addCase(unfavoriteGame.rejected, (state, action) => {
      // reset favorite status when service call fails
      const gameId = action.meta.arg;
      state.authenticated.byId[gameId].displayFeatures.Favorite = {};
    });
    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

const persistConfig = {
  key: 'games',
  storage: AsyncStorage,
  whitelist: Platform.OS === 'web' ? [] : ['authenticated'],
  version: 2,
};

if (Platform.OS === 'web') {
  localStorage.removeItem('mmkv.default\\persist:games');
}

const persistedReducer = persistReducer(persistConfig, games.reducer);

export const withGames = injectSlice({
  reducerPath: 'games',
  reducer: persistedReducer,
});

export const selectGame = (id: Game['id']) =>
  withGames.selector(
    (state) => state.games[state.app.authenticationStatus].byId[id],
  );

export const selectGames = withGames.selector(
  (state) => state.games[state.app.authenticationStatus].byId,
);

export const selectGameIds = withGames.selector(
  (state) => state.games[state.app.authenticationStatus].ids,
);

export const selectGameIdByName = createSelector(
  [(_, name: Game['name']) => name, selectGameIds, selectGames],
  (name, gameIds, games) => gameIds.find((id) => games[id].name === name),
);

export const selectCoinGameIds = createSelector(
  [selectGameIds, selectGames],
  (gameIds, games) =>
    gameIds.filter((gameId) => {
      const game = games[gameId];
      return game && game.hasCoinsGameplay;
    }),
);

export const selectGamesState = withGames.selector(
  (state) => state.games[state.app.authenticationStatus].dataState,
);

export const selectSubProviderById = (id: number) => {
  return TOP_SLOT_PROVIDERS[id as keyof typeof TOP_SLOT_PROVIDERS];
};

export const selectProviderGames = createSelector(
  [(_, provider: string) => provider, selectGames, selectGameIds],
  (provider, games, gameIds) =>
    gameIds.filter((id) => games[id].subprovider === provider),
);

export const selectGameReleaseDate = (id: Game['id']) =>
  withGames.selector(
    (state) => selectGame(id)(state)?.displayFeatures.ComingSoon?.validUntil,
  );

export const selectAvailableGames = createSelector(
  [selectGameIds, selectGames],
  (gameIds, games) =>
    gameIds.reduce((acc: Game[], gameId) => {
      const game = games[gameId];

      if (game) {
        const releaseDate = game.displayFeatures.ComingSoon?.validUntil;
        const lockedLabel = getLockedLabel(releaseDate);

        if (!lockedLabel) {
          acc.push(game);
        }
      }

      return acc;
    }, []),
);

declare global {
  interface LazyLoadedSlices {
    games: ReturnType<typeof persistedReducer>;
  }
}
